import { formatShortDate } from '@/lib/formatDate'
import { ALL_TIME_FROM, type TransactionLinkFilters } from '@/lib/transactionsLink'

function monthName(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
}

export function periodLabel(f: TransactionLinkFilters): string {
  if (f.spendMonthFrom) {
    const to = f.spendMonthTo ?? f.spendMonthFrom
    return to === f.spendMonthFrom
      ? `For spending in ${monthName(f.spendMonthFrom)}`
      : `For spending in ${monthName(f.spendMonthFrom)} – ${monthName(to)}`
  }
  if (f.dateFrom && f.dateTo) return `${formatShortDate(f.dateFrom)} – ${formatShortDate(f.dateTo)}`
  if (f.dateFrom === ALL_TIME_FROM) return 'All time'
  if (f.dateFrom) return `Since ${formatShortDate(f.dateFrom)}`
  if (f.dateTo) return `Until ${formatShortDate(f.dateTo)}`
  if (f.date) {
    return new Date(`${f.date}T00:00:00`).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }
  return f.month ? monthName(f.month) : ''
}

export type Lead = 'net' | 'spent' | 'income' | 'oneoff'

export function leadFor(f: TransactionLinkFilters): Lead {
  if (f.isCapitalEvent) return 'oneoff'
  if (f.type === 'EXPENSE') return 'spent'
  if (f.type === 'INCOME') return 'income'
  return 'net'
}

export const LEAD_LABEL: Record<Lead, string> = {
  net: 'Net',
  spent: 'Spent',
  income: 'Income',
  oneoff: 'One-off, net',
}

function parseDay(date: string): Date {
  return new Date(`${date}T00:00:00`)
}

/** "Today · Thu 16 Oct", "Yesterday · Wed 15 Oct", "Tue 14 Oct", with the year only when it differs. */
export function dayLabel(date: string, now = new Date()): string {
  const d = parseDay(date)
  const sameYear = d.getFullYear() === now.getFullYear()
  const label = d.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const diffDays = Math.round((startOfToday - d.getTime()) / 86_400_000)
  if (diffDays === 0) return `Today · ${label}`
  if (diffDays === 1) return `Yesterday · ${label}`
  return label
}
